import { useEffect, useState, useCallback, useRef } from "react";
import { createPortal } from "react-dom";
import { MessageSquarePlus, Bot } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import TemplateDialog from "@/components/conversations/TemplateDialog";
import api, { formatApiErrorDetail } from "@/lib/api";
import { useToast } from "@/hooks/use-toast";
import { CONVERSATIONS } from "@/constants/testIds";
import ConversationList from "@/components/conversations/ConversationList";
import ConversationDetail from "@/components/conversations/ConversationDetail";
import TagManagerDialog from "@/components/conversations/TagManagerDialog";
import ContactsExportDialog from "@/components/conversations/ContactsExportDialog";
import { CONVERSATION_TAGS, CONVERSATION_STAGES, CONVERSATION_STATUS_LABELS, stageLabel, tagLabel } from "@/lib/conversationsConstants";
import { useCompactLayout, useVisibleViewport, useChatHistoryEntry } from "@/hooks/use-compact-layout";

const POLL_MS = 8000;

/** Inbox de WhatsApp (lista + detalle con polling): reusado tanto en la
 * pagina standalone /conversaciones (Recepcion) como en la pestaña
 * "Conversaciones" del panel completo del Agente (/agente, Admin). */
export default function ConversationsPanel({ heightClass = "h-[calc(100vh-8rem)]" }) {
  const [conversations, setConversations] = useState([]);
  const [filter, setFilter] = useState("all");
  const [unreadOnly, setUnreadOnly] = useState(false); // Fix BG (2026-11-04): filtro "No leídos"
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  // Fix O (2026-09-30): ventana de conversacion nueva.
  const [newOpen, setNewOpen] = useState(false);
  const { toast } = useToast();
  const selectedRef = useRef(null);
  selectedRef.current = selectedId;

  // Fix AI (2026-10-23): en el celular se ve una sola columna: la lista, o el chat
  // abierto a pantalla completa con un boton para volver. El boton "atras" del
  // celular tambien cierra el chat (en vez de salir de Conversaciones).
  const compact = useCompactLayout();
  const chatOpenCompact = compact && Boolean(selectedId);
  const viewport = useVisibleViewport(chatOpenCompact);
  // Ajuste de la aplicacion (CAMBIO 46): la entrada de historial del chat abierto tiene un unico dueno
  // y se limpia sola (ver useChatHistoryEntry en use-compact-layout.js); la flecha solo cierra el chat.
  const handleBack = useCallback(() => setSelectedId(null), []);
  useChatHistoryEntry(chatOpenCompact, handleBack);

  // Fix AF (2026-10-19): busqueda por telefono (o nombre) en todos los chats.
  // Se espera a que se deje de escribir y solo cuenta la ultima respuesta.
  const [search, setSearch] = useState("");
  const [searchQ, setSearchQ] = useState("");
  const listReq = useRef(0);
  useEffect(() => {
    const t = setTimeout(() => setSearchQ(search.trim()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const loadList = useCallback(async () => {
    const req = ++listReq.current;
    try {
      const params = { ...(filter !== "all" ? { status: filter } : {}), ...(searchQ ? { q: searchQ } : {}), ...(unreadOnly ? { unread: true } : {}) };
      const res = await api.get("/conversations", { params });
      if (req === listReq.current) setConversations(res.data);
    } catch {
      // silencioso en polling; la carga inicial ya muestra el estado vacio
    }
  }, [filter, searchQ, unreadOnly]);

  const loadDetail = useCallback(async (id) => {
    try {
      const res = await api.get(`/conversations/${id}`);
      if (selectedRef.current === id) setDetail(res.data);
      // Fix BG (2026-11-04): abrir el chat (o tenerlo abierto cuando llega un mensaje) lo marca como leido.
      if (selectedRef.current === id && res.data.unread_count > 0) {
        await api.post(`/conversations/${id}/read`);
        setConversations((list) => list.map((c) => (c.id === id ? { ...c, unread_count: 0 } : c)));
      }
    } catch (err) {
      toast({ title: "Error al cargar la conversacion", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  }, [toast]);

  useEffect(() => { loadList(); }, [loadList]);
  useEffect(() => {
    const interval = setInterval(loadList, POLL_MS);
    return () => clearInterval(interval);
  }, [loadList]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    loadDetail(selectedId);
    const interval = setInterval(() => loadDetail(selectedId), POLL_MS);
    return () => clearInterval(interval);
  }, [selectedId, loadDetail]);

  // Fix N (2026-09-30): devuelven true/false para que la caja de texto no se
  // borre si el envio falla.
  const handleSendReply = async (body) => {
    try {
      await api.post(`/conversations/${selectedId}/reply`, { body });
      await Promise.all([loadDetail(selectedId), loadList()]);
      return true;
    } catch (err) {
      toast({ title: "No se pudo enviar el mensaje", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      return false;
    }
  };

  const handleSendMedia = async (file, caption, voice) => {
    const form = new FormData();
    form.append("file", file);
    form.append("caption", caption || "");
    form.append("voice", voice ? "true" : "false");
    try {
      await api.post(`/conversations/${selectedId}/reply-media`, form, { headers: { "Content-Type": "multipart/form-data" }, timeout: 120000 });
      await Promise.all([loadDetail(selectedId), loadList()]);
      return true;
    } catch (err) {
      toast({ title: "No se pudo enviar el archivo", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      return false;
    }
  };

  // Fix O (2026-09-30): plantillas (ventana de 24 h cerrada) y conversacion nueva.
  const handleSendTemplate = async ({ template, language, params }) => {
    try {
      await api.post(`/conversations/${selectedId}/template`, { template, language, params });
      await Promise.all([loadDetail(selectedId), loadList()]);
      return true;
    } catch (err) {
      toast({ title: "No se pudo enviar la plantilla", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      return false;
    }
  };

  // Fix AP (2026-10-23): respuesta del equipo por SMS
  const handleSendSms = async (body) => {
    try {
      await api.post(`/conversations/${selectedId}/reply`, { body, channel: "sms" });
      await Promise.all([loadDetail(selectedId), loadList()]);
      return true;
    } catch (err) {
      toast({ title: "No se pudo enviar el SMS", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      await loadDetail(selectedId);
      return false;
    }
  };

  const handleStartConversation = async (values) => {
    try {
      const { data } = await api.post("/conversations/start", values);
      setFilter("all");
      setSelectedId(data.conversation_id);
      await loadList();
      return true;
    } catch (err) {
      toast({ title: "No se pudo iniciar la conversacion", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
      return false;
    }
  };

  const handleChangeStatus = async (status) => {
    try {
      await api.patch(`/conversations/${selectedId}/status`, { status });
      await Promise.all([loadDetail(selectedId), loadList()]);
    } catch (err) {
      toast({ title: "No se pudo actualizar la conversacion", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  // Fix Q (2026-10-01): interruptor general del agente (el mismo "Agente activo"
  // de Agente IA) y el de cada chat, que manda sobre el general.
  const [agentOn, setAgentOn] = useState(null);
  useEffect(() => {
    api.get("/conversations/agent-switch").then((res) => setAgentOn(res.data.enabled)).catch(() => setAgentOn(null));
  }, []);

  // Fix AP (2026-10-23): si los SMS estan prendidos, el chat deja elegir por donde responder.
  const [smsOn, setSmsOn] = useState(false);
  useEffect(() => {
    api.get("/sms/status").then((res) => setSmsOn(Boolean(res.data.enabled))).catch(() => setSmsOn(false));
  }, []);

  const handleToggleAgent = async (enabled) => {
    try {
      const res = await api.put("/conversations/agent-switch", { enabled });
      setAgentOn(res.data.enabled);
      toast({ title: enabled ? "Agente prendido en todos los chats" : "Agente apagado en todos los chats" });
    } catch (err) {
      toast({ title: "No se pudo cambiar el agente", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  const handleToggleChatAgent = async (enabled) => {
    try {
      await api.patch(`/conversations/${selectedId}/agent`, { enabled });
      await Promise.all([loadDetail(selectedId), loadList()]);
    } catch (err) {
      toast({ title: "No se pudo cambiar el agente en este chat", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  // Fix AD (2026-10-15): etapa, etiquetas y servicio de interes de cada chat, y
  // filtros por esos datos en la lista (sin etapa cuenta como "Nuevo").
  // Fix AG (2026-10-21): etapas y servicios de interes salen de las listas que
  // edita el equipo (los servicios, con la forma {id, name, is_active} de antes).
  const [interestList, setInterestList] = useState([]);
  const [stageList, setStageList] = useState(CONVERSATION_STAGES);
  const services = interestList.map((s) => ({ id: s.key, name: s.label, is_active: !s.hidden }));
  const [classFilter, setClassFilter] = useState({ stage: "all", tag: "all", service: "all" });
  useEffect(() => {
    api.get("/conversations/interests").then((res) => setInterestList(res.data.interests)).catch(() => setInterestList([]));
    api.get("/conversations/stages").then((res) => setStageList(res.data.stages)).catch(() => {});
  }, []);
  const visibleConversations = conversations.filter((c) =>
    (classFilter.stage === "all" || (c.stage || "nuevo") === classFilter.stage)
    && (classFilter.tag === "all" || (c.tags || []).includes(classFilter.tag))
    && (classFilter.service === "all" || c.interest_service_id === classFilter.service));

  const handleClassify = async (changes) => {
    try {
      await api.patch(`/conversations/${selectedId}/classification`, changes);
      await Promise.all([loadDetail(selectedId), loadList()]);
    } catch (err) {
      toast({ title: "No se pudo guardar la clasificacion", description: formatApiErrorDetail(err.response?.data?.detail), variant: "destructive" });
    }
  };

  // Fix AE (2026-10-19): etiquetas que edita el equipo y Excel de los contactos
  // con los filtros de la lista.
  const [tagList, setTagList] = useState(CONVERSATION_TAGS);
  const [listKind, setListKind] = useState("tags");
  const [listOpen, setListOpen] = useState(false);
  const openList = (kind) => { setListKind(kind); setListOpen(true); };
  const [exportOpen, setExportOpen] = useState(false);
  useEffect(() => {
    api.get("/conversations/tags").then((res) => setTagList(res.data.tags)).catch(() => {});
  }, []);
  const handleListSaved = (items) => {
    const field = { tags: "tag", stages: "stage", interests: "service" }[listKind];
    if (listKind === "stages") setStageList(items);
    else if (listKind === "interests") setInterestList(items);
    else setTagList(items);
    if (classFilter[field] !== "all" && !items.some((t) => t.key === classFilter[field])) setClassFilter((f) => ({ ...f, [field]: "all" }));
    toast({ title: { tags: "Etiquetas guardadas", stages: "Etapas guardadas", interests: "Servicios de interés guardados" }[listKind] });
    if (selectedId) loadDetail(selectedId);
  };
  const exportParams = {
    ...(filter !== "all" ? { status: filter } : {}),
    ...(classFilter.stage !== "all" ? { stage: classFilter.stage } : {}),
    ...(classFilter.tag !== "all" ? { tag: classFilter.tag } : {}),
    ...(classFilter.service !== "all" ? { service: classFilter.service } : {}),
    ...(searchQ ? { q: searchQ } : {}),
  };
  const exportSummary = [
    filter !== "all" ? CONVERSATION_STATUS_LABELS[filter] || filter : null,
    classFilter.stage !== "all" ? `Etapa: ${stageLabel(classFilter.stage, stageList)}` : null,
    classFilter.tag !== "all" ? `Etiqueta: ${tagLabel(classFilter.tag, tagList)}` : null,
    classFilter.service !== "all" ? `Servicio: ${(services.find((s) => s.id === classFilter.service) || {}).name || ""}` : null,
    searchQ ? `Búsqueda: "${searchQ}"` : null,
  ].filter(Boolean);

  const detailEl = (
    <ConversationDetail conversation={compact ? (detail && detail.id === selectedId ? detail : null) : detail} onSendReply={handleSendReply} onSendMedia={handleSendMedia} onSendTemplate={handleSendTemplate} onChangeStatus={handleChangeStatus} agentGlobalOn={Boolean(agentOn)} onToggleAgent={handleToggleChatAgent} smsEnabled={smsOn} onSendSms={handleSendSms} services={services} onClassify={handleClassify} tagList={tagList} stageList={stageList} onBack={chatOpenCompact ? handleBack : undefined} />
  );

  return (
    <div className={`flex ${heightClass}`} data-testid={CONVERSATIONS.page}>
      <div className="flex-1 flex gap-4 min-h-0 min-w-0">
        {/* Fix AI (2026-10-23): en el celular la columna de la lista ocupa todo el ancho y se desplaza entera */}
        <div className={compact ? "w-full min-w-0 flex flex-col gap-2 min-h-0 overflow-y-auto" : "w-80 shrink-0 flex flex-col gap-2 min-h-0"}>
          <div className="flex items-center justify-between gap-3 bg-white border border-border rounded-xl px-3 py-2 shrink-0">
            <div className="flex items-center gap-2 min-w-0">
              <Bot className="w-4 h-4 text-muted-foreground shrink-0" />
              <div className="min-w-0">
                <p className="text-sm font-medium text-foreground">Agente IA</p>
                <p className="text-xs text-muted-foreground" data-testid={CONVERSATIONS.agentGlobalStatus}>
                  {agentOn ? "Prendido en todos los chats" : "Apagado. Se puede prender en un chat"}
                </p>
              </div>
            </div>
            <Switch checked={Boolean(agentOn)} disabled={agentOn === null} onCheckedChange={handleToggleAgent} data-testid={CONVERSATIONS.agentGlobalSwitch} />
          </div>
          <Button onClick={() => setNewOpen(true)} className={`bg-brand hover:bg-brand-hover text-white gap-1.5 shrink-0 ${compact ? "h-11" : ""}`} data-testid={CONVERSATIONS.newConversationButton}>
            <MessageSquarePlus className="w-4 h-4" /> Nueva conversacion
          </Button>
          <div className={compact ? "shrink-0" : "flex-1 min-h-0"}>
            <ConversationList
              conversations={visibleConversations}
              selectedId={selectedId}
              onSelect={setSelectedId}
              filter={filter}
              onFilterChange={setFilter}
              unreadOnly={unreadOnly}
              onUnreadOnlyChange={setUnreadOnly}
              services={services}
              classFilter={classFilter}
              onClassFilterChange={setClassFilter}
              tagList={tagList}
              stageList={stageList}
              onManageList={openList}
              onExport={() => setExportOpen(true)}
              search={search}
              onSearchChange={setSearch}
            />
          </div>
        </div>
        {!compact && detailEl}
        {/* Fix AI (2026-10-23): chat a pantalla completa (sobre toda la app) que sigue a la parte visible: queda sobre el teclado */}
        {chatOpenCompact && createPortal(
          <div className="fixed inset-x-0 top-0 bottom-0 z-40 flex flex-col bg-background p-1.5" style={viewport ? { top: viewport.top, height: viewport.height, bottom: "auto" } : undefined} data-testid={CONVERSATIONS.mobileChat}>
            {detailEl}
          </div>,
          document.body,
        )}
        <TagManagerDialog open={listOpen} onOpenChange={setListOpen} kind={listKind} tags={listKind === "stages" ? stageList : listKind === "interests" ? interestList : tagList} onSaved={handleListSaved} />
        <ContactsExportDialog open={exportOpen} onOpenChange={setExportOpen} params={exportParams} summary={exportSummary} />
        <TemplateDialog open={newOpen} onOpenChange={setNewOpen} title="Nueva conversacion" askPhone preferred="retomar" onSend={handleStartConversation} />
      </div>
    </div>
  );
}
